import { useNavigate } from "react-router-dom";
import { ChevronLeft, Delete, Loader2 } from "lucide-react";
import { inr } from "@/lib/api";

const TONE = { SUCCESS: "bg-emerald-100 text-emerald-800", APPROVED: "bg-emerald-100 text-emerald-800", PENDING: "bg-amber-100 text-amber-800",
  FAILED: "bg-red-100 text-red-700", REJECTED: "bg-red-100 text-red-700", REFUNDED: "bg-sky-100 text-sky-800", CANCELLED: "bg-slate-200 text-slate-600" };

export function Chip({ s, testid }) {
  return <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${TONE[s] || "bg-slate-100 text-slate-600"}`} data-testid={testid || "m-status-chip"}>{s}</span>;
}

export const rs = (v) => inr(v || 0);

export function Screen({ title, back = true, right, children, testid, dark }) {
  const nav = useNavigate();
  const head = dark ? "bg-[#0F2C47] text-white" : "bg-white/95 text-[#0F2C47] border-b border-slate-100";
  return (
    <div className="min-h-full bg-white pb-28" data-testid={testid}>
      <div className={`sticky top-0 z-20 grid h-14 grid-cols-[48px_1fr_48px] items-center px-2 backdrop-blur-md ${head}`}>
        {back ? <button onClick={() => nav(-1)} className="grid h-10 w-10 place-items-center rounded-full active:bg-black/5" data-testid="m-back"><ChevronLeft className={`h-6 w-6 ${dark ? "text-[#F26B1D]" : ""}`} /></button> : <span />}
        <h1 className="truncate text-center font-display text-lg font-bold">{title}</h1>
        <div className="flex justify-end">{right}</div>
      </div>
      <div className="px-4 pt-4">{children}</div>
    </div>
  );
}

export function Btn({ children, loading, className = "", variant = "primary", ...p }) {
  const v = variant === "primary" ? "bg-[#0F2C47] text-white active:bg-[#14304d]" : variant === "ghost" ? "bg-white text-[#0B1F33] border border-black/10" : "bg-emerald-600 text-white";
  return (
    <button {...p} disabled={loading || p.disabled} className={`flex h-14 w-full items-center justify-center gap-2 rounded-2xl text-base font-bold transition-transform active:scale-[0.98] disabled:opacity-50 ${v} ${className}`}>
      {loading && <Loader2 className="h-5 w-5 animate-spin" />}{children}
    </button>
  );
}

export function Input({ label, hint, className = "", ...p }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <input {...p} className={`h-14 w-full rounded-2xl border border-black/10 bg-white px-4 text-base outline-none focus:border-[#0B1F33] ${className}`} />
      {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

export function Sel({ label, options, ...p }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <select {...p} className="h-14 w-full rounded-2xl border border-black/10 bg-white px-4 text-base outline-none">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

export function Card({ children, className = "", ...p }) {
  return <div {...p} className={`rounded-3xl border border-black/5 bg-white p-4 shadow-[0_1px_0_rgba(0,0,0,0.03)] ${className}`}>{children}</div>;
}

export function PinPad({ value, onChange, length = 4, testid = "m-pinpad" }) {
  const press = (d) => value.length < length && onChange(value + d);
  return (
    <div data-testid={testid}>
      <div className="mb-6 flex justify-center gap-4">
        {Array.from({ length }).map((_, i) => <span key={i} className={`h-4 w-4 rounded-full border-2 border-[#0B1F33] ${i < value.length ? "bg-[#0B1F33]" : ""}`} />)}
      </div>
      <div className="mx-auto grid max-w-[280px] grid-cols-3 gap-3">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"].map((k) => k === "" ? <span key="e" /> : (
          <button key={k} type="button" onClick={() => (k === "del" ? onChange(value.slice(0, -1)) : press(k))}
            className="grid h-16 place-items-center rounded-2xl bg-white text-2xl font-semibold shadow-sm active:bg-slate-100" data-testid={`${testid}-${k}`}>
            {k === "del" ? <Delete className="h-6 w-6" /> : k}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Empty({ text }) {
  return <div className="py-12 text-center text-sm text-slate-400" data-testid="m-empty">{text}</div>;
}
